/**
 * Express crypto — cifra/descifra importes EN EL NAVEGADOR usando el PIN del módulo.
 *
 * IMPORTANTE — modelo de amenazas (iter 67):
 * - El objetivo es que, si alguien abre MongoDB Compass o ve un dump de la BD,
 *   los importes aparezcan como ruido base64 incomprensible.
 * - NO es una garantía criptográfica fuerte: el PIN típico (4-6 dígitos) es
 *   brute-forceable en segundos si un atacante motivado obtiene el blob.
 * - El PIN NUNCA sale del navegador. El backend solo guarda/devuelve el blob.
 *
 * Algoritmo:
 *   key  = PBKDF2-SHA256(PIN, salt=APP_SALT, iter=120_000, length=256)
 *   iv   = random 12 bytes
 *   ct   = AES-GCM(key, iv, json.dumps({"v":1, "amount":<number>}))
 *   blob = base64( iv || ct )
 *
 * El salt es constante (no es secreto). El PIN es lo único secreto.
 */

const enc = new TextEncoder();
const dec = new TextDecoder();

// Salt fijo de la app (no es un secreto). 16 bytes aleatorios fijados en código.
const APP_SALT = Uint8Array.from([
  0x52, 0x8a, 0xd4, 0x11, 0x9c, 0x3e, 0x6b, 0x77,
  0xa5, 0xcc, 0x0f, 0x42, 0x83, 0xee, 0x21, 0x9d,
]);
const PBKDF2_ITER = 120_000;
const ALGO = "AES-GCM";
const IV_LEN = 12;

function b64FromBytes(bytes) {
  let s = "";
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s);
}

function bytesFromB64(b64) {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** Deriva una CryptoKey AES-GCM-256 a partir del PIN. Reutiliza durante la sesión. */
export async function deriveKeyFromPin(pin) {
  if (!pin) throw new Error("PIN vacío");
  const raw = enc.encode(String(pin));
  const baseKey = await crypto.subtle.importKey(
    "raw", raw, { name: "PBKDF2" }, false, ["deriveKey"],
  );
  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: APP_SALT,
      iterations: PBKDF2_ITER,
      hash: "SHA-256",
    },
    baseKey,
    { name: ALGO, length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

/** Cifra un número (importe en €). Devuelve string base64 listo para guardar. */
export async function encryptAmount(amount, key) {
  if (!key) throw new Error("Clave no disponible");
  const n = Number(amount);
  if (!Number.isFinite(n)) throw new Error("Importe inválido");
  const payload = JSON.stringify({ v: 1, amount: n, t: Date.now() });
  const iv = crypto.getRandomValues(new Uint8Array(IV_LEN));
  const ct = new Uint8Array(
    await crypto.subtle.encrypt({ name: ALGO, iv }, key, enc.encode(payload)),
  );
  const combined = new Uint8Array(IV_LEN + ct.length);
  combined.set(iv, 0);
  combined.set(ct, IV_LEN);
  return b64FromBytes(combined);
}

/**
 * Descifra un blob. Devuelve `null` si el PIN no es el correcto o el blob es inválido.
 * Si el blob viene de un PIN anterior (rotado), el AES-GCM tag falla y devuelve null.
 */
export async function decryptAmount(blob, key) {
  if (!blob || !key) return null;
  try {
    const bytes = bytesFromB64(blob);
    if (bytes.length <= IV_LEN) return null;
    const iv = bytes.slice(0, IV_LEN);
    const ct = bytes.slice(IV_LEN);
    const pt = await crypto.subtle.decrypt({ name: ALGO, iv }, key, ct);
    const obj = JSON.parse(dec.decode(pt));
    if (obj && typeof obj.amount === "number") return obj.amount;
    return null;
  } catch (_e) {
    return null;
  }
}

/** Formato EUR consistente. */
export function fmtEur(n) {
  if (n == null || !Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("es-ES", {
    style: "currency", currency: "EUR", maximumFractionDigits: 2,
  }).format(n);
}
